'use client';

import { useEffect, useState, useCallback, useRef, type ReactNode, type FC } from 'react';
import { logger } from '../utils/logger';
import { AdMeshSDK } from '../sdk/AdMeshSDK';
import { AdMeshContext, type AdMeshContextValue } from './AdMeshContext';
import type { AdMeshTheme, PlatformResponse, ActiveDelegationSession } from '../types/index';
import { extractHeadingsDirectly, type HeadingsExtractionResult } from '../utils/extractHeadings';
import { callAnalyzeIntentEndpoint } from '../utils/acieApi';
import { fetchPageContext } from '../utils/pageContextApi';
import type { Suggestion } from '../components/AdMeshIntentAssistant';

export type PageContextMode = 'page' | 'ad-slot' | 'auto';

function isInIframe(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function getUsableWindowHref(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const href = window.location.href?.trim();
    if (isResolvedArticleUrl(href)) {
      return href;
    }
  } catch {
    return null;
  }
  return null;
}

function isResolvedArticleUrl(value?: string): boolean {
  const trimmed = value?.trim();
  if (!trimmed || /%%[^%]+%%/.test(trimmed)) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function titleFromUrlPath(url: string): string {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const raw = segments[segments.length - 1] || segments[segments.length - 2] || parsed.hostname;
    const cleaned = raw
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned ? cleaned.replace(/\b\w/g, (char) => char.toUpperCase()) : parsed.hostname;
  } catch {
    return 'This page';
  }
}

function buildUrlFallbackContext(url: string): HeadingsExtractionResult {
  const parsed = new URL(url);
  const title = titleFromUrlPath(url);
  return {
    title,
    headings: [title],
    article_text: `Article URL: ${url}\nArticle topic inferred from URL path: ${title}`,
    url,
    domain: parsed.hostname,
    extracted_at: new Date().toISOString(),
  };
}

const DELEGATION_HEARTBEAT_INTERVAL_MS = 60_000;
const DELEGATION_IDLE_HEARTBEATS_BEFORE_STOP = 3;

function resolveArticleUrlForContext(articleUrl?: string): string | null {
  const currentHref = getUsableWindowHref();
  if (currentHref) {
    return currentHref;
  }
  const trimmed = articleUrl?.trim();
  if (isResolvedArticleUrl(trimmed)) {
    return trimmed;
  }
  return null;
}

export interface AdMeshProviderProps {
  /** AdMesh API key (required) */
  apiKey: string;

  /** Session ID (required) */
  sessionId: string;

  /** Optional: Whether this provider is being used by the extension (optional) */
  extension?: boolean;

  /** Optional theme configuration */
  theme?: AdMeshTheme;

  /** Optional API base URL (defaults to production) */
  apiBaseUrl?: string;

  /** Optional user language in BCP 47 format (e.g., "en-US") */
  language?: string;

  /** Optional detailed user location */
  location?: {
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    zipcode?: string;
    latitude?: number;
    longitude?: number;
  };

  /** Optional anonymous hashed user ID */
  userId?: string;

  /** Optional AI model identifier (e.g., "gpt-4o") - forwarded as platform software version metadata */
  model?: string;

  /** Optional conversation history - forwarded in classification_input.interaction.input.messages */
  messages?: Array<{ role: string; content: string; id?: string }>;

  /** Pre-resolved page context (skips client/server extract) */
  pageContext?: HeadingsExtractionResult | null;

  /** Article URL for ad-slot / GAM (e.g. %%PAGE_URL%% macro) */
  articleUrl?: string;

  /** How to obtain page context: page DOM, ad-slot API, or auto-detect iframe */
  contextMode?: PageContextMode;

  /** Agent display id for optional content-index lookup */
  agentDisplayId?: string;

  /** Child components */
  children: ReactNode;
}

/**
 * AdMeshProvider - Simplified SDK integration for React applications
 *
 * Handles:
 * - SDK initialization and lifecycle management
 * - Message deduplication tracking
 * - Session and message ID management
 * - Error handling and logging
 *
 * @example
 * ```tsx
 * <AdMeshProvider
 *   apiKey={process.env.NEXT_PUBLIC_ADMESH_API_KEY}
 *   sessionId="custom-session-id"
 * >
 *   <Chat messages={messages} />
 * </AdMeshProvider>
 * 
 * // Extension usage (skips intent analysis)
 * <AdMeshProvider 
 *   apiKey="pub_extension_key"
 *   sessionId="extension-session-id"
 *   extension={true}
 * >
 *   <AdMeshIntentAssistant autoOpen={true} />
 * </AdMeshProvider>
 * ```
 */
export const AdMeshProvider: FC<AdMeshProviderProps> = ({
  apiKey,
  sessionId,
  extension,
  theme,
  apiBaseUrl,
  language,
  location,
  userId,
  model,
  messages,
  pageContext,
  articleUrl,
  contextMode = 'auto',
  agentDisplayId,
  children,
}) => {
  const [sdk, setSdk] = useState<AdMeshSDK | null>(null);
  const [processedMessageIds, setProcessedMessageIds] = useState<Set<string>>(
    new Set()
  );
  const [recommendationResults, setRecommendationResults] = useState<
    Map<string, PlatformResponse | null>
  >(new Map());
  const [activeDelegationSession, setActiveDelegationSessionState] = useState<ActiveDelegationSession | null>(null);

  // Suggestion state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isAnalyzingIntent, setIsAnalyzingIntent] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [isResolvingPageContext, setIsResolvingPageContext] = useState(false);
  const hasAttemptedInitialAnalysisRef = useRef(false);
  const heartbeatsSinceLastUserTurnRef = useRef(0);
  const trackedDelegationSessionIdRef = useRef<string | null>(null);
  const activeDelegationSessionRef = useRef<ActiveDelegationSession | null>(null);

  activeDelegationSessionRef.current = activeDelegationSession;

  const usesAdSlotContext =
    Boolean(pageContext) ||
    contextMode === 'ad-slot' ||
    (contextMode === 'auto' && (Boolean(articleUrl?.trim()) || isInIframe()));

  const resolveHeadingsForAnalysis = useCallback(async (): Promise<HeadingsExtractionResult | null> => {
    if (pageContext) {
      return pageContext;
    }

    if (!usesAdSlotContext) {
      return extractHeadingsDirectly();
    }

    const targetUrl = resolveArticleUrlForContext(articleUrl);
    if (!targetUrl) {
      logger.warn('[AdMeshProvider] Ad-slot context mode requires a real article URL');
      setIntentError('Article URL is required for ad-slot context');
      return null;
    }

    if (!apiKey || !apiBaseUrl) {
      logger.warn('[AdMeshProvider] Cannot fetch page context: missing API key or base URL');
      return buildUrlFallbackContext(targetUrl);
    }

    try {
      setIsResolvingPageContext(true);
      const response = await fetchPageContext(targetUrl, {
        apiKey,
        apiBaseUrl: apiBaseUrl || 'https://api.useadmesh.com',
        agentDisplayId,
      });
      logger.log('[AdMeshProvider] Page context resolved', {
        source: response.source,
        cache_hit: response.cache_hit,
        domain: response.context.domain,
      });
      return response.context;
    } catch (error) {
      logger.error('[AdMeshProvider] Failed to fetch page context:', error);
      logger.warn('[AdMeshProvider] Falling back to URL-path context for suggestions');
      return buildUrlFallbackContext(targetUrl);
    } finally {
      setIsResolvingPageContext(false);
    }
  }, [
    agentDisplayId,
    apiBaseUrl,
    apiKey,
    articleUrl,
    pageContext,
    usesAdSlotContext,
  ]);

  const prependSponsoredSuggestion = useCallback(
    (currentSuggestions: Suggestion[], sponsoredSuggestion: Suggestion): Suggestion[] => {
      const organicSuggestions = currentSuggestions.filter((suggestion) => !suggestion.is_sponsored).slice(0, 3);
      return [sponsoredSuggestion, ...organicSuggestions];
    },
    [],
  );

  const mergeOrganicSuggestions = useCallback((currentSuggestions: Suggestion[], organicSuggestions: Suggestion[]) => {
    const sponsoredSuggestion = currentSuggestions.find((suggestion) => suggestion.is_sponsored);
    const nextOrganicSuggestions = organicSuggestions.filter((suggestion) => !suggestion.is_sponsored).slice(0, 3);
    return sponsoredSuggestion ? [sponsoredSuggestion, ...nextOrganicSuggestions] : nextOrganicSuggestions;
  }, []);

  // Validate that sessionId is provided by platform
  useEffect(() => {
    if (!sessionId || sessionId.trim() === '') {
      logger.error('[AdMeshProvider] ❌ sessionId is required and must be provided by the platform. Use AdMeshSDK.generateSessionIdFromUrl() for URL-based detection.');
      return;
    }
    
    logger.log('[AdMeshProvider] ✅ Session ID available:', sessionId);
  }, [sessionId]);

  // Initialize SDK once on mount
  useEffect(() => {
    if (!apiKey) {
      logger.warn('[AdMeshProvider] ⚠️ AdMesh API key not configured');
      return;
    }

    if (!sessionId || sessionId.trim() === '') {
      logger.error('[AdMeshProvider] ❌ Cannot initialize SDK: sessionId is required. Use AdMeshSDK.generateSessionIdFromUrl() for URL-based detection.');
      return;
    }

    try {
      const sdkInstance = new AdMeshSDK({
        apiKey,
        theme,
        apiBaseUrl,
        extension,
      });
      setSdk(sdkInstance);
      logger.log('[AdMeshProvider] ✅ AdMesh SDK initialized with session:', sessionId);
      if (apiBaseUrl) {
        logger.log('[AdMeshProvider] 📍 Using custom API base URL');
      }
    } catch (error) {
      logger.error('[AdMeshProvider] ❌ Failed to initialize AdMesh SDK:', error);
    }

    // Cleanup on unmount
    return () => {
      logger.log('[AdMeshProvider] 🧹 Provider unmounted');
    };
  }, [apiKey, theme, apiBaseUrl, sessionId, extension]);

  // Intent analysis function
  const performIntentAnalysis = useCallback(async (prefetchedHeadingsData?: HeadingsExtractionResult | null) => {
    if (!apiKey || !apiBaseUrl) {
      logger.warn('[AdMeshProvider] ⚠️ Cannot perform intent analysis: missing API key or base URL');
      return;
    }

    // Skip intent analysis if called from extension
    if (extension) {
      logger.log('[AdMeshProvider] ℹ️ Extension mode: allowing intent analysis for any API key');
      // Don't return - continue to intent analysis for extension
    } else {
      // For non-extension usage, only allow pub_ API keys
      if (!apiKey.toLowerCase().startsWith('pub_')) {
        logger.log('[AdMeshProvider] ℹ️ Skipping auto intent analysis: API key does not start with "pub_"');
        return;
      }
    }

    try {
      setIsAnalyzingIntent(true);
      setIntentError(null);
      
      logger.log('[AdMeshProvider] 🔄 Starting automatic intent analysis...');
      
      const headingsData = prefetchedHeadingsData ?? (await resolveHeadingsForAnalysis());
      if (!headingsData) {
        logger.warn('[AdMeshProvider] ⚠️ Failed to extract headings for intent analysis');
        setIntentError('Failed to extract page content');
        return;
      }
      
      logger.log('[AdMeshProvider] 📄 Extracted headings:', {
        title: headingsData.title,
        headingsCount: headingsData.headings.length,
        articleTextLength: headingsData.article_text?.length || 0,
        domain: headingsData.domain
      });
      
      // Call ACIE API for intent analysis
      const config = {
        apiKey,
        apiBaseUrl: apiBaseUrl || 'https://api.useadmesh.com',
        extension,
        userId
      };
      
      const analysisResult = await callAnalyzeIntentEndpoint(headingsData, config);
      
      if (analysisResult) {
        logger.log('[AdMeshProvider] ✅ Intent analysis completed:', {
          suggestionsCount: analysisResult.suggestions.length,
          suggestions: (analysisResult.suggestions || []).map((suggestion) => ({
            id: suggestion.id,
            text: suggestion.text,
            is_sponsored: suggestion.is_sponsored,
            recommendationId: suggestion.recommendation?.recommendation_id || null,
          })),
        });
        
        setSuggestions((currentSuggestions) =>
          mergeOrganicSuggestions(currentSuggestions, analysisResult.suggestions || [])
        );
      } else {
        logger.warn('[AdMeshProvider] ⚠️ Intent analysis returned null');
        setIntentError('Intent analysis failed');
        setSuggestions((current) => {
          if (current.length > 0) {
            logger.log('[AdMeshProvider] ℹ️ Preserving existing suggestions after null intent analysis result');
            return current;
          }
          return [
            {
              id: "fallback-1",
              text: "What are your questions about this content?",
              type: "information" as const,
              is_sponsored: false,
              recommendation: undefined,
            }
          ];
        });
      }
    } catch (error) {
      logger.error('[AdMeshProvider] ❌ Error in intent analysis:', error);
      setIntentError('Intent analysis error');
      setSuggestions((current) => {
        if (current.length > 0) {
          logger.log('[AdMeshProvider] ℹ️ Preserving existing suggestions after intent analysis error');
          return current;
        }
        return [
          {
            id: "error-fallback-1",
            text: "Ask me questions about this page",
            type: "information" as const,
            is_sponsored: false,
            recommendation: undefined,
          }
        ];
      });
    } finally {
      setIsAnalyzingIntent(false);
    }
  }, [apiKey, apiBaseUrl, extension, userId, mergeOrganicSuggestions, resolveHeadingsForAnalysis]);

  const prefetchSponsoredSuggestion = useCallback(
    async (prefetchedHeadingsData?: HeadingsExtractionResult | null) => {
      if (!sdk || !apiKey) {
        return;
      }

      if (!apiKey.toLowerCase().startsWith('pub_')) {
        return;
      }

      const headingsData = prefetchedHeadingsData ?? (await resolveHeadingsForAnalysis());
      if (!headingsData) {
        logger.warn('[AdMeshProvider] ⚠️ Failed to extract page content for sponsored suggestion prefetch');
        return;
      }

      const query = headingsData.headings?.[0]?.trim() || headingsData.title?.trim();
      if (!query) {
        logger.warn('[AdMeshProvider] ⚠️ Skipping sponsored suggestion prefetch: no usable page query');
        return;
      }

      try {
        logger.log('[AdMeshProvider] 🔄 Prefetching sponsored suggestion from /aip/context...');
        const platformResult = await sdk.fetchPlatformResponse({
          query,
          sessionId,
          messageId: AdMeshSDK.createMessageId(sessionId),
          platformSurface: 'embedded_assistant',
          model,
          messages: [],
          language,
          location,
          userId,
        });

        const suggestionText = platformResult.recommendation?.creative?.follow_up_suggestion?.trim();
        const recommendationId = platformResult.recommendation_id;
        if (!suggestionText || !recommendationId) {
          logger.log('[AdMeshProvider] ℹ️ No sponsored follow-up suggestion returned from /aip/context');
          return;
        }

        const sponsoredSuggestion: Suggestion = {
          id: recommendationId,
          text: suggestionText,
          type: 'action',
          is_sponsored: true,
          recommendation: platformResult,
          metadata: {
            source: 'aip_context_prefetch',
            recommendation_id: recommendationId,
          },
        };

        setSuggestions((currentSuggestions) =>
          prependSponsoredSuggestion(currentSuggestions, sponsoredSuggestion)
        );

        logger.log('[AdMeshProvider] ✅ Sponsored suggestion prefetched from /aip/context', {
          recommendationId,
          suggestionText,
        });
      } catch (error) {
        logger.warn('[AdMeshProvider] ⚠️ Sponsored suggestion prefetch failed (non-blocking):', error);
      }
    },
    [apiKey, language, location, model, sdk, sessionId, userId, prependSponsoredSuggestion, resolveHeadingsForAnalysis],
  );

  // Retry intent analysis function
  const retryIntentAnalysis = useCallback(async () => {
    logger.log('[AdMeshProvider] 🔄 Manual retry of intent analysis');
    await performIntentAnalysis();
  }, [performIntentAnalysis]);

  // Auto-analyze intent once on mount for public API keys.
  // Empty ACIE/no_match responses are still valid outcomes and must not retrigger forever.
  useEffect(() => {
    if (!sdk || hasAttemptedInitialAnalysisRef.current) {
      return;
    }

    hasAttemptedInitialAnalysisRef.current = true;
    void (async () => {
      const headingsData = await resolveHeadingsForAnalysis();
      await Promise.allSettled([
        prefetchSponsoredSuggestion(headingsData),
        performIntentAnalysis(headingsData),
      ]);
    })();
  }, [sdk, performIntentAnalysis, prefetchSponsoredSuggestion, resolveHeadingsForAnalysis]);

  const isDelegationSessionActive = useCallback((session: ActiveDelegationSession | null) => {
    if (!session || session.status !== 'active') {
      return false;
    }

    const expiresAtMs = Date.parse(session.expires_at);
    if (Number.isNaN(expiresAtMs)) {
      return false;
    }

    return expiresAtMs > Date.now();
  }, []);

  const postDelegationEvent = useCallback(
    async (
      endpoint: string,
      eventType: string,
      metadata?: Record<string, unknown>,
      sessionOverride?: ActiveDelegationSession | null,
    ) => {
      const session = sessionOverride ?? activeDelegationSession;
      if (!session || !sdk) {
        logger.warn('[AdMeshProvider] ⚠️ Skipping delegation event because session or SDK is missing', {
          endpoint,
          eventType,
          hasSession: !!session,
          hasSdk: !!sdk,
        });
        return;
      }

      const timestamp = new Date().toISOString();
      logger.log('[AdMeshProvider] 📡 Sending delegation event', {
        endpoint,
        eventType,
        recommendationId: session.recommendation_id,
        delegationSessionId: session.delegation_session_id,
        sessionId: session.session_id,
      });
      const response = await fetch(`${sdk.getApiBaseUrl()}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          delegation_session_id: session.delegation_session_id,
          recommendation_id: session.recommendation_id,
          session_id: session.session_id,
          event_type: eventType,
          timestamp,
          metadata: metadata || {},
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        logger.error('[AdMeshProvider] ❌ Delegation event failed', {
          endpoint,
          eventType,
          status: response.status,
          errorBody,
          recommendationId: session.recommendation_id,
          delegationSessionId: session.delegation_session_id,
        });
        throw new Error(`Delegation event failed (${response.status}): ${errorBody}`);
      }

      const result = await response.json();
      const updatedSession = result?.session as ActiveDelegationSession | undefined;
      logger.log('[AdMeshProvider] ✅ Delegation event acknowledged', {
        endpoint,
        eventType,
        recommendationId: session.recommendation_id,
        delegationSessionId: session.delegation_session_id,
        returnedStatus: updatedSession?.status || result?.status,
      });
      if (updatedSession) {
        setActiveDelegationSessionState(updatedSession);
      }
    },
    [activeDelegationSession, apiKey, sdk],
  );

  const recordDelegationUserTurn = useCallback(() => {
    heartbeatsSinceLastUserTurnRef.current = 0;
  }, []);

  const stopDelegationSession = useCallback(
    async (reason: string = 'user_stopped', metadata?: Record<string, unknown>) => {
      const session = activeDelegationSession;
      if (!session) {
        logger.warn('[AdMeshProvider] ⚠️ stopDelegationSession called without an active SDK delegation session');
        return;
      }

      logger.log('[AdMeshProvider] 🛑 Stopping delegation session', {
        reason,
        recommendationId: session.recommendation_id,
        delegationSessionId: session.delegation_session_id,
      });
      await postDelegationEvent(
        '/aip/delegation/stop',
        'session_stopped',
        { reason, ...(metadata || {}) },
        session,
      );
      await sdk?.trackPublisherAssistantEvent({
        event_name: 'delegation_stopped',
        timestamp: new Date().toISOString(),
        session_id: session.session_id,
        recommendation_id: session.recommendation_id,
        delegation_session_id: session.delegation_session_id,
        brand_name: session.brand_name,
        status: 'stopped',
        metadata: {
          reason,
          ...(metadata || {}),
        },
      }).catch((analyticsError) => {
        logger.warn('[AdMeshProvider] ⚠️ Failed to track delegation_stopped event:', analyticsError);
      });
      heartbeatsSinceLastUserTurnRef.current = 0;
      trackedDelegationSessionIdRef.current = null;
      setActiveDelegationSessionState(null);
      logger.log('[AdMeshProvider] ✅ Delegation session cleared locally after stop');
    },
    [activeDelegationSession, postDelegationEvent, sdk],
  );

  const refreshDelegationActivity = useCallback(
    async (eventType: string = 'turn_completed', metadata?: Record<string, unknown>) => {
      if (eventType === 'turn_started') {
        recordDelegationUserTurn();
      }
      await postDelegationEvent('/aip/delegation/activity', eventType, metadata);
    },
    [postDelegationEvent, recordDelegationUserTurn],
  );

  useEffect(() => {
    const delegationSessionId = activeDelegationSession?.delegation_session_id ?? null;
    if (delegationSessionId !== trackedDelegationSessionIdRef.current) {
      trackedDelegationSessionIdRef.current = delegationSessionId;
      heartbeatsSinceLastUserTurnRef.current = 0;
    }
  }, [activeDelegationSession?.delegation_session_id]);

  useEffect(() => {
    const delegationSessionId = activeDelegationSession?.delegation_session_id ?? null;
    if (!delegationSessionId || !sdk) {
      return;
    }

    const session = activeDelegationSessionRef.current;
    if (!session || !isDelegationSessionActive(session)) {
      setActiveDelegationSessionState((prev) =>
        prev
          ? {
              ...prev,
              status: 'expired',
            }
          : null,
      );
      return;
    }

    const interval = window.setInterval(() => {
      const currentSession = activeDelegationSessionRef.current;
      if (!currentSession || !isDelegationSessionActive(currentSession)) {
        return;
      }

      heartbeatsSinceLastUserTurnRef.current += 1;
      if (heartbeatsSinceLastUserTurnRef.current >= DELEGATION_IDLE_HEARTBEATS_BEFORE_STOP) {
        logger.log('[AdMeshProvider] ⏱️ Stopping delegation after idle heartbeats', {
          delegationSessionId: currentSession.delegation_session_id,
          heartbeatsSinceLastUserTurn: heartbeatsSinceLastUserTurnRef.current,
          idleHeartbeatLimit: DELEGATION_IDLE_HEARTBEATS_BEFORE_STOP,
        });
        void stopDelegationSession('idle_timeout', {
          source: 'heartbeat_idle',
          heartbeats_without_turn: heartbeatsSinceLastUserTurnRef.current,
        }).catch((error) => {
          logger.warn('[AdMeshProvider] ⚠️ Failed to stop idle delegation session:', error);
        });
        return;
      }

      void postDelegationEvent('/aip/delegation/heartbeat', 'heartbeat').catch((error) => {
        logger.warn('[AdMeshProvider] ⚠️ Failed to send delegation heartbeat:', error);
      });
    }, DELEGATION_HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [
    activeDelegationSession?.delegation_session_id,
    isDelegationSessionActive,
    postDelegationEvent,
    sdk,
    stopDelegationSession,
  ]);

  // Create context value
  const contextValue: AdMeshContextValue = {
    sdk,
    apiKey,
    sessionId,
    theme,
    language,
    location,
    userId,
    model,
    messages,
    processedMessageIds,
    recommendationResults,
    activeDelegationSession,
    
    // Suggestion state
    suggestions,
    isAnalyzingIntent: isAnalyzingIntent || isResolvingPageContext,
    intentError,
    
    // Methods
    markMessageAsProcessed: (messageId: string) => {
      setProcessedMessageIds((prev) => {
        const updated = new Set(prev);
        updated.add(messageId);
        return updated;
      });
    },
    
    isMessageProcessed: (messageId: string) => {
      return processedMessageIds.has(messageId);
    },

    setRecommendationResult: (messageId: string, result: PlatformResponse | null) => {
      setRecommendationResults((prev) => {
        const next = new Map(prev);
        next.set(messageId, result);
        return next;
      });
      setProcessedMessageIds((prev) => {
        const updated = new Set(prev);
        updated.add(messageId);
        return updated;
      });
    },

    getRecommendationResult: (messageId: string) => {
      return recommendationResults.get(messageId);
    },

    hasRecommendationResult: (messageId: string) => {
      return recommendationResults.has(messageId);
    },

    setActiveDelegationSession: (session: ActiveDelegationSession | null) => {
      setActiveDelegationSessionState(session);
    },

    getActiveDelegationSession: () => {
      if (!isDelegationSessionActive(activeDelegationSession)) {
        return null;
      }
      return activeDelegationSession;
    },

    hasActiveDelegationSession: () => {
      return isDelegationSessionActive(activeDelegationSession);
    },

    refreshDelegationActivity,

    stopDelegationSession,
    
    retryIntentAnalysis,
  };

  return (
    <AdMeshContext.Provider value={contextValue}>
      {children}
    </AdMeshContext.Provider>
  );
};

export default AdMeshProvider;
